import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CategorySelection from './CategorySelection';
import Browse from './Browse';
import BrowseBuyOrders from './BrowseBuyOrders';
import BuyOrder from './BuyOrder';
import SellOrder from './SellOrder';
import History from './History';
import ActiveOrders from './ActiveOrders';
import categories from '../../types/categories';
import './AuctionHouse.css';

const TAB_CONFIG = [
    { key: 'Browse',        icon: '⚔',  label: 'Browse' },
    { key: 'Buy Orders',    icon: '📋', label: 'Buy Orders' },
    { key: 'Buy Order',     icon: '📜', label: 'Buy Order' },
    { key: 'Sell Order',    icon: '💰', label: 'Sell Order' },
    { key: 'Active Orders', icon: '⏳', label: 'My Orders' },
    { key: 'History',       icon: '📖', label: 'History' },
];

const AuctionHouse = () => {
    const [activeTab, setActiveTab] = useState('Browse');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<keyof typeof categories | null>(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [isBuilt, setIsBuilt] = useState<boolean | null>(null);

    useEffect(() => {
        axios.get('/api/city-buildings/auction-house/status', { withCredentials: true })
            .then(res => setIsBuilt(res.data.isBuilt))
            .catch(() => setIsBuilt(true));
    }, []);

    if (isBuilt === null) {
        return (
            <div className="ah-loading">
                <div className="ah-loading-spinner" />
                <span>Opening the Trading Hall...</span>
            </div>
        );
    }

    if (!isBuilt) {
        return (
            <div className="auction-house ah-not-built">
                <div className="ah-not-built-inner">
                    <h2>The Trading Hall Awaits</h2>
                    <p>
                        Deals are whispered in taverns and coins change hands in shadow.<br />
                        The official market has yet to be built, until the city completes its 
                        trading square, no sanctioned trading can take place.
                    </p>
                    <p className="ah-not-built-hint">
                        Deliver resources through City Projects to raise the Auction House.
                    </p>
                    <Link to="/city-projects" className="ah-not-built-btn">
                        Go to City Projects
                    </Link>
                </div>
            </div>
        );
    }

    const handleCategorySelect = (category: keyof typeof categories | null, subcategory?: string) => {
        setSelectedCategory(category);
        setSelectedSubcategory(subcategory || null);
        setSearchTerm('');
    };

    const handleSearchChange = (term: string) => {
        setSearchTerm(term);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'Browse':
                return <Browse category={selectedCategory} subcategory={selectedSubcategory} searchTerm={searchTerm} />;
            case 'Buy Orders':
                return <BrowseBuyOrders category={selectedCategory} subcategory={selectedSubcategory} searchTerm={searchTerm} />;
            case 'Buy Order':
                return <BuyOrder />;
            case 'Sell Order':
                return <SellOrder />;
            case 'History':
                return <History />;
            case 'Active Orders':
                return <ActiveOrders />;
            default:
                return <Browse category={selectedCategory} subcategory={selectedSubcategory} searchTerm={searchTerm} />;
        }
    };

    return (
        <div className="auction-house">
            {/* ── Decorative Header ── */}
            <header className="ah-header">
                <div className="ah-header-ornament left">⸻</div>
                <div className="ah-header-center">
                    <h1 className="ah-title">Auction House</h1>
                    <span className="ah-subtitle">Trade amongst warriors and merchants</span>
                </div>
                <div className="ah-header-ornament right">⸻</div>
            </header>

            {/* ── Tab Navigation ── */}
            <nav className="ah-tabs">
                {TAB_CONFIG.map(({ key, icon, label }) => (
                    <button
                        key={key}
                        className={`ah-tab ${activeTab === key ? 'active' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        <span className="ah-tab-icon">{icon}</span>
                        <span className="ah-tab-label">{label}</span>
                    </button>
                ))}
            </nav>

            {/* ── Mobile Controls ── */}
            <div className="ah-mobile-controls">
                <button
                    className="ah-mobile-filter-toggle"
                    onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                >
                    {showMobileSidebar ? '✕ Hide Filters' : '☰ Show Filters'}
                </button>
            </div>

            {/* ── Main Content ── */}
            <div className="ah-body">
                <aside className={`ah-sidebar ${showMobileSidebar ? 'mobile-visible' : ''}`}>
                    <CategorySelection onCategorySelect={handleCategorySelect} onSearchChange={handleSearchChange} />
                </aside>
                <main className="ah-content">
                    {renderTabContent()}
                </main>
            </div>
        </div>
    );
};

export default AuctionHouse;
